import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatINR } from '@/lib/format';
import { listSisStores } from '@/api/sisStock';
import { previewSisSettlement, generateSisSettlement, generateGroupSisSettlement, previewGroupSisSettlement, type SisSettlement } from '@/api/sisSettlement';

const iso = (d: Date) => d.toISOString().slice(0, 10);

interface Props {
  open: boolean;
  onClose: () => void;
}

export function GenerateSettlementModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<'store' | 'group'>('store');
  const [partyId, setPartyId] = useState('');
  const [group, setGroup] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [tds, setTds] = useState('');
  const [salary, setSalary] = useState(''); // blank = use the store's monthly salary
  const [groupTds, setGroupTds] = useState(''); // single TDS for the whole group

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const from = new Date(now);
    from.setDate(from.getDate() - 15);
    setMode('store');
    setPartyId('');
    setGroup('');
    setStart(iso(from));
    setEnd(iso(now));
    setTds('');
    setSalary('');
    setGroupTds('');
  }, [open]);

  const storesQ = useQuery({ queryKey: ['sis-stores'], queryFn: () => listSisStores(), enabled: open, staleTime: 0 });
  const stores = storesQ.data ?? [];
  const storeOptions: SearchableOption[] = stores.map((s) => ({
    value: String(s.id),
    label: s.name,
    sublabel: s.city || undefined,
  }));
  const groupOptions = Array.from(new Set(stores.map((s) => s.group).filter(Boolean))).sort();

  const ready = mode === 'store' ? !!partyId && !!start && !!end : !!group && !!start && !!end;
  const previewQ = useQuery({
    queryKey: ['sis-settle-preview', partyId, start, end],
    queryFn: () => previewSisSettlement(Number(partyId), start, end),
    enabled: open && mode === 'store' && !!partyId && !!start && !!end,
  });
  const p = previewQ.data;

  const groupPreviewQ = useQuery({
    queryKey: ['sis-settle-group-preview', group, start, end],
    queryFn: () => previewGroupSisSettlement(group, start, end),
    enabled: open && mode === 'group' && !!group && !!start && !!end,
  });
  const gp = groupPreviewQ.data;
  const groupStores = gp?.stores ?? [];
  const groupTdsN = Number(groupTds) || 0;
  const groupFinal = gp ? Math.round((gp.totals.netReceivable - gp.totals.salaryDeduction - groupTdsN) * 100) / 100 : 0;
  const canGenerate = mode === 'store'
    ? ready && !previewQ.isFetching
    : !!group && !!start && !!end && !!gp && !gp.alreadySettled && gp.totals.txnCount > 0 && !groupPreviewQ.isFetching;

  const salaryVal = salary !== '' ? Number(salary) : (p?.salaryDeduction ?? 0);
  const tdsVal = Number(tds) || 0;
  const finalVal = p ? Math.round((p.netReceivable - salaryVal - tdsVal) * 100) / 100 : 0;

  const gen = useMutation<SisSettlement, unknown, void>({
    mutationFn: () =>
      mode === 'group'
        ? generateGroupSisSettlement({ group, periodStart: start, periodEnd: end, tdsAmount: groupTdsN })
        : generateSisSettlement({
            partyId: Number(partyId),
            periodStart: start,
            periodEnd: end,
            tdsAmount: tdsVal,
            salaryDeduction: salary !== '' ? Number(salary) : undefined,
          }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['sis-settlements'] });
      toast.push(`Settlement ${res.settlementNo} generated`, 'success');
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Could not generate settlement'), 'error'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Generate settlement"
      size="md"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canGenerate || gen.isPending} onClick={() => gen.mutate()}>
            {gen.isPending ? 'Generating…' : mode === 'group' ? 'Generate group' : 'Generate'}
          </Button>
        </>
      }
    >
      {/* Store vs whole-group toggle */}
      <div className="mb-3 inline-flex rounded-lg border border-[var(--border-color)] p-0.5">
        {(['store', 'group'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={
              'rounded-md px-3 py-1 text-xs font-bold transition ' +
              (mode === m ? 'bg-accent text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]')
            }
          >
            {m === 'store' ? 'Single store' : 'Whole group'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {mode === 'store' ? (
          <FormGroup label="Store" className="sm:col-span-3">
            <SearchableSelect
              options={storeOptions}
              value={partyId}
              onChange={(v) => setPartyId(v === '' ? '' : String(v))}
              placeholder="Type a store name…"
            />
          </FormGroup>
        ) : (
          <FormGroup label="Group" className="sm:col-span-3">
            <Select value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">Select a group…</option>
              {groupOptions.map((g) => <option key={g}>{g}</option>)}
            </Select>
          </FormGroup>
        )}
        <FormGroup label="Period from">
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </FormGroup>
        <FormGroup label="Period to">
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </FormGroup>
        {mode === 'store' && (
          <>
            <FormGroup label="Salesperson salary (₹)">
              <Input type="number" min="0" value={salary} onChange={(e) => setSalary(e.target.value)} placeholder={p ? String(p.salaryDeduction) : 'store default'} />
            </FormGroup>
            <FormGroup label="TDS (₹)">
              <Input type="number" min="0" value={tds} onChange={(e) => setTds(e.target.value)} placeholder="0" />
            </FormGroup>
          </>
        )}
        {mode === 'group' && (
          <FormGroup label="TDS for whole group (₹)">
            <Input type="number" min="0" value={groupTds} onChange={(e) => setGroupTds(e.target.value)} placeholder="0" />
          </FormGroup>
        )}
      </div>

      {mode === 'group' && group && (
        <div className="mt-3 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-3 backdrop-blur-xl">
          {groupPreviewQ.isFetching ? (
            <p className="text-sm text-[var(--text-muted)]">Loading {group}…</p>
          ) : !gp || groupStores.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">No stores in {group}.</p>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                  {group} — consolidated ({gp.totals.storesWithSales}/{gp.totals.storeCount} stores with sales)
                </span>
                {gp.alreadySettled && <span className="text-[10px] text-warning">already settled for this period</span>}
              </div>
              {/* Per-store breakdown (salary auto from Assets Master) */}
              <div className="max-h-[30vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                      <th className="px-2 py-1">Store</th>
                      <th className="px-2 py-1 text-right">Gross</th>
                      <th className="px-2 py-1 text-right">Net</th>
                      <th className="px-2 py-1 text-right">Salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupStores.map((s) => (
                      <tr key={s.partyId} className={'border-t border-[var(--border-color)]/50 ' + (s.txnCount === 0 ? 'opacity-50' : '')}>
                        <td className="px-2 py-1">{s.storeName}{s.txnCount === 0 && <span className="ml-1 text-[10px] text-[var(--text-muted)]">no sales</span>}</td>
                        <td className="px-2 py-1 text-right">₹{formatINR(s.grossSale)}</td>
                        <td className="px-2 py-1 text-right">₹{formatINR(s.netReceivable)}</td>
                        <td className="px-2 py-1 text-right">₹{formatINR(s.salaryDeduction)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Consolidated totals */}
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-[var(--border-color)] pt-3 text-sm sm:grid-cols-3">
                <Stat label="Units sold" value={gp.totals.unitsSold} />
                <Stat label="Gross sale" value={`₹${formatINR(gp.totals.grossSale)}`} />
                <Stat label={`Commission (${gp.totals.commissionPct}%)`} value={`₹${formatINR(gp.totals.commissionAmt)}`} />
                <Stat label="Net receivable" value={`₹${formatINR(gp.totals.netReceivable)}`} />
                <Stat label="− Salaries (Σ)" value={`₹${formatINR(gp.totals.salaryDeduction)}`} />
                <Stat label="− TDS (group)" value={`₹${formatINR(groupTdsN)}`} />
                <Stat label="Final receivable" value={`₹${formatINR(groupFinal)}`} strong />
              </dl>
            </>
          )}
        </div>
      )}

      {/* Live preview of the expected settlement (single store) */}
      {mode === 'store' && ready && (
        <div className="mt-4 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-4 backdrop-blur-xl">
          {previewQ.isFetching ? (
            <p className="text-sm text-[var(--text-muted)]">Calculating…</p>
          ) : p ? (
            <>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                Expected settlement · {p.storeName}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
                <Stat label="Units sold" value={p.unitsSold} />
                <Stat label="Transactions" value={p.txnCount} />
                <Stat label="Gross sale" value={`₹${formatINR(p.grossSale)}`} />
                <Stat label={`Commission (${p.commissionPct}%)`} value={`₹${formatINR(p.commissionAmt)}`} />
                <Stat label="Net receivable" value={`₹${formatINR(p.netReceivable)}`} />
                <Stat label="− Salesperson salary" value={`₹${formatINR(salaryVal)}`} />
                <Stat label="− TDS" value={`₹${formatINR(tdsVal)}`} />
                <Stat label="Final receivable" value={`₹${formatINR(finalVal)}`} strong />
              </dl>
              {!p.commissionSet && (
                <p className="mt-2 text-xs text-warning">
                  No commission % set for {p.group || 'this group'} — treated as 0%. Set it in Commission &amp; Settlement.
                </p>
              )}
              {p.txnCount === 0 && (
                <p className="mt-2 text-xs text-[var(--text-muted)]">No sales in this period — settlement will be zero.</p>
              )}
            </>
          ) : (
            <p className="text-sm text-danger">Could not load preview.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value, strong }: { label: string; value: string | number; strong?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-[var(--text-muted)]">{label}</dt>
      <dd className={strong ? 'text-base font-bold text-accent' : 'font-medium'}>{value}</dd>
    </div>
  );
}
